import { useRef, useState } from "react";
import styled from "styled-components";
import { Button, Tabs } from "antd";
import { BoxStyle } from "../../style/styleValue";
import FreeTextEditor from "./FreeTextEditor";
import { hasFreeTextContent } from "./freeTextConfig";

interface Props {
  postId: number;
  initialHtml?: string | null;
  initialHtmlEn?: string | null;
  // "영문 버전 입력하기" 가 켜져 있으면 한글 / 영문 탭으로 나눠 입력
  englishEnabled: boolean;
  onSave: (html: string | null, htmlEn: string | null) => Promise<void>;
}

// 게시글 작성 화면의 FreeText 본문 입력 (블록 방식 Editor 와 같은 틀)
const FreeTextPostForm = ({ postId, initialHtml, initialHtmlEn, englishEnabled, onSave }: Props) => {
  // 편집기 입력마다 다시 그릴 필요가 없어 ref 로만 들고 있는다
  const htmlRef = useRef<string>(initialHtml ?? "");
  const htmlEnRef = useRef<string>(initialHtmlEn ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const html = hasFreeTextContent(htmlRef.current) ? htmlRef.current : null;
      const htmlEn = englishEnabled && hasFreeTextContent(htmlEnRef.current) ? htmlEnRef.current : null;
      await onSave(html, htmlEn);
    } finally {
      setSaving(false);
    }
  };

  const koreanEditor = (
    <FreeTextEditor
      postId={postId}
      initialHtml={htmlRef.current}
      onChange={(html) => (htmlRef.current = html)}
      placeholder="내용을 입력하세요."
    />
  );

  return (
    <div style={{ ...BoxStyle, margin: "0 auto", maxWidth: "60rem", minHeight: "100vh" }}>
      <Card>
        <CardHeader>내용</CardHeader>
        {englishEnabled ? (
          <Tabs
            className="freetext-language-tabs"
            items={[
              { key: "ko", label: "한국어", children: koreanEditor },
              {
                key: "en",
                label: "English",
                children: (
                  <FreeTextEditor
                    postId={postId}
                    initialHtml={htmlEnRef.current}
                    onChange={(html) => (htmlEnRef.current = html)}
                    placeholder="Enter the English version."
                  />
                ),
              },
            ]}
          />
        ) : (
          koreanEditor
        )}
      </Card>
      <SaveBar>
        <Button type="primary" onClick={save} loading={saving}>
          Save
        </Button>
      </SaveBar>
    </div>
  );
};

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;

  .freetext-language-tabs .ant-tabs-nav {
    margin: 0;
    padding: 0 1rem;
  }
`;

const CardHeader = styled.div`
  padding: 0.75rem 1.25rem;
  background-color: #f7f7f9;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
`;

// 글이 길어도 저장 버튼이 화면 아래에 붙어 있도록 한다
const SaveBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 100;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.5rem 0;
  background-color: #fff;
`;

export default FreeTextPostForm;
